import { memo, useCallback, useContext } from 'react';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GridOnIcon from '@mui/icons-material/GridOn';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { Tabs, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CONTENT_VIEW } from 'constants/content';
import { AppContext } from 'context';

const ContentViewForm = memo(() => {
    const { t } = useTranslation();
    const { setContentView, contentView, contentViews } = useContext(AppContext);

    const tabsHandleChange = useCallback(
        (event: React.SyntheticEvent, newValue: string) => {
            if (
                newValue === CONTENT_VIEW.List ||
                newValue === CONTENT_VIEW.Calendar ||
                newValue === CONTENT_VIEW.Grid
            ) {
                setContentView(newValue);
            }
        },
        [setContentView]
    );

    // Only one view available -> don't show navigation
    if (contentViews.length <= 1) {
        return null;
    }

    return (
        <Tabs
            aria-label={t('accessibility.view_selector')}
            value={contentView}
            onChange={tabsHandleChange}
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{
                '& .MuiTabs-flexContainer': {
                    maxWidth: '100%',
                    flexWrap: 'wrap',
                    columnGap: '0.75rem',
                },
                '& .MuiTab-root': {
                    minHeight: 'auto',
                    px: 0,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 400,
                    lineHeight: 1.5,
                    transform: 'translateX(-0.085rem)',
                    '&.Mui-selected': {
                        color: 'primary.main',
                    },
                    '&:not(.Mui-selected)': {
                        color: 'black',
                        '& .icon': {
                            color: '#666666',
                        },
                    },
                },
                '& .MuiTabs-indicator': {
                    backgroundColor: 'primary.main',
                },
            }}
        >
            {contentViews.includes(CONTENT_VIEW.List) && (
                <Tab
                    value={CONTENT_VIEW.List}
                    data-testid='list-tab'
                    icon={<ListAltOutlinedIcon className='icon' />}
                    label={t('form.list_view')}
                    iconPosition='start'
                />
            )}
            {contentViews.includes(CONTENT_VIEW.Grid) && (
                <Tab
                    value={CONTENT_VIEW.Grid}
                    data-testid='grid-tab'
                    icon={<GridOnIcon className='icon' />}
                    label={t('form.grid_view')}
                    iconPosition='start'
                />
            )}
            {contentViews.includes(CONTENT_VIEW.Calendar) && (
                <Tab
                    value={CONTENT_VIEW.Calendar}
                    data-testid='calendar-tab'
                    icon={<CalendarTodayIcon className='icon' />}
                    label={t('form.calendar_view')}
                    iconPosition='start'
                />
            )}
        </Tabs>
    );
});

export default ContentViewForm;
